// src/services/workHoursAudit.js
//
// Tracciabilità delle modifiche manuali al cartellino (collection `workHours`).
//
// Tutti i pannelli che permettono di correggere le ore a mano (Admin/HR,
// Responsabile, Capo Officina, correzione da segnalazione/rapportino) salvano
// passando da `applyManualWorkHoursChanges`, che:
// - scrive SOLO i giorni realmente modificati, in transazione, sopra lo stato
//   attuale del database: non riscrive il mese intero e non crea giorni vuoti,
//   quindi non cancella le timbrature arrivate nel frattempo;
// - aggiunge alla nota del giorno chi ha modificato, quando e i valori
//   prima → dopo;
// - accoda la modifica allo storico del giorno (`entry.history`).
//
// In più la Cloud Function `onWorkHoursWritten` (functions/workHoursAudit.js)
// registra lato server in `workHoursAudit` OGNI variazione del cartellino, da
// chiunque arrivi, con l'utente autenticato che l'ha scritta.

import { doc, getDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';

const SPECIAL_LETTERS = ['M', 'P', 'F', 'A', 'C'];

export const isSpecialLetter = (value) =>
  typeof value === 'string' && SPECIAL_LETTERS.includes(value.trim().toUpperCase());

// Ore del giorno: lettera speciale oppure numero (vuoto = 0)
export const normalizeTotal = (value) => {
  const text = String(value ?? '').trim().toUpperCase();
  if (SPECIAL_LETTERS.includes(text)) return text;
  const number = parseFloat(text.replace(',', '.'));
  return Number.isFinite(number) ? number : 0;
};

export const normalizeOvertime = (value) => {
  const number = parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isFinite(number) ? number : 0;
};

// "8+2", oppure la lettera speciale
export const formatHours = (total, overtime) =>
  isSpecialLetter(total) ? total : `${total}+${overtime}`;

// I pannelli Responsabile/Capo Officina aggiungono "(modificato da ...)" mentre
// si digita: la nota completa la scrive questo modulo al salvataggio
const cleanNotes = (notes) =>
  String(notes ?? '').replace(/\(modificato da [^)]+\)/g, '').replace(/\s+/g, ' ').trim();

const formatStamp = (date) => date.toLocaleString('it-IT', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
});

/**
 * Fotografia dei valori caricati dal database, da confrontare al salvataggio
 * per capire quali giorni sono stati modificati.
 */
export const snapshotEntries = (entries) => (entries || []).map(entry => ({
  date: entry.date,
  total: normalizeTotal(entry.total),
  overtime: isSpecialLetter(normalizeTotal(entry.total)) ? 0 : normalizeOvertime(entry.overtime),
  notes: cleanNotes(entry.notes)
}));

/**
 * Giorni modificati a video rispetto alla fotografia fatta al caricamento.
 */
export const diffEditedEntries = (originalSnapshot, editedEntries) => {
  const originalByDate = {};
  (originalSnapshot || []).forEach(entry => { originalByDate[entry.date] = entry; });

  return snapshotEntries(editedEntries)
    .map((edited, index) => ({ edited, source: editedEntries[index] }))
    .filter(({ edited }) => {
      const original = originalByDate[edited.date] || { total: 0, overtime: 0, notes: '' };
      return formatHours(original.total, original.overtime) !== formatHours(edited.total, edited.overtime) ||
        original.notes !== edited.notes;
    })
    .map(({ source }) => source);
};

/**
 * Chi sta modificando: l'utente autenticato, con nome e ruolo letti da `users`.
 */
const resolveEditor = async (editor) => {
  const user = auth.currentUser;
  const id = editor?.id || user?.uid || null;
  let name = editor?.name || null;
  let role = editor?.role || null;

  if (id && (!name || !role)) {
    try {
      const userSnap = await getDoc(doc(db, 'users', id));
      if (userSnap.exists()) {
        const data = userSnap.data();
        name = name || (data.nome && data.cognome ? `${data.nome} ${data.cognome}` : data.email);
        role = role || data.role || null;
      }
    } catch (error) {
      console.warn('workHoursAudit: impossibile leggere i dati di chi modifica:', error);
    }
  }

  return { id, name: name || user?.email || 'utente sconosciuto', role };
};

/**
 * Applica al cartellino le modifiche manuali di uno o più giorni.
 *
 * @param {Object} params
 * @param {string} params.userId - dipendente a cui appartiene il cartellino
 * @param {string|number} params.month
 * @param {string|number} params.year
 * @param {Array} params.changes - giorni modificati: { date, total, overtime, notes, day?, isWeekend?, extra? }
 * @param {string} params.source - schermata di provenienza (es. 'pannello-admin')
 * @param {Object} [params.editor] - { id, name, role }; se assente si usa l'utente autenticato
 * @returns {Promise<{changedDays: number, applied: Array}>}
 */
export const applyManualWorkHoursChanges = async ({ userId, month, year, changes, source, editor }) => {
  if (!userId || !month || !year) throw new Error('Dipendente, mese e anno sono obbligatori');

  const who = await resolveEditor(editor);
  const normalizedMonth = String(month).replace(/^0+/, '') || String(month);
  const workHoursRef = doc(db, 'workHours', `${userId}_${normalizedMonth}_${year}`);
  const now = new Date();
  const nowIso = now.toISOString();

  return runTransaction(db, async (transaction) => {
    const snap = await transaction.get(workHoursRef);
    const entries = snap.exists() ? [...(snap.data().entries || [])] : [];
    const applied = [];

    for (const change of changes || []) {
      if (!change?.date) continue;

      const index = entries.findIndex(entry => entry.date === change.date);
      const current = index >= 0 ? entries[index] : null;
      const before = {
        total: normalizeTotal(current?.total),
        overtime: normalizeOvertime(current?.overtime),
        notes: String(current?.notes ?? '')
      };

      const total = normalizeTotal(change.total);
      const overtime = isSpecialLetter(total) ? 0 : normalizeOvertime(change.overtime);
      const userNotes = cleanNotes(change.notes);

      const beforeHours = current ? formatHours(before.total, before.overtime) : 'nessun dato';
      const afterHours = formatHours(total, overtime);
      const hoursChanged = formatHours(before.total, before.overtime) !== afterHours;
      const notesChanged = cleanNotes(before.notes) !== userNotes;
      if (!hoursChanged && !notesChanged) continue;

      const notes = hoursChanged
        ? [userNotes, `[Modifica manuale ${formatStamp(now)} - ${who.name}: ${beforeHours} → ${afterHours}]`]
          .filter(Boolean).join(' ')
        : userNotes;

      const entry = {
        ...(current || {}),
        ...(change.extra || {}),
        date: change.date,
        total,
        overtime,
        notes,
        isWeekend: change.isWeekend ?? current?.isWeekend ?? false,
        manualEdit: true,
        lastModified: nowIso,
        modifiedBy: who.id,
        modifierName: who.name,
        history: [
          ...(current?.history || []),
          {
            at: nowIso,
            source,
            byId: who.id,
            byName: who.name,
            byRole: who.role,
            before: current ? before : null,
            after: { total, overtime, notes }
          }
        ]
      };
      if (change.day !== undefined || current?.day !== undefined) {
        entry.day = change.day ?? current.day;
      }

      if (index >= 0) entries[index] = entry;
      else entries.push(entry);
      applied.push({ date: change.date, before: beforeHours, after: afterHours });
    }

    if (applied.length === 0) return { changedDays: 0, applied };

    entries.sort((a, b) => String(a.date).localeCompare(String(b.date)));
    const lastManualEdit = { at: nowIso, byId: who.id, byName: who.name, source };

    if (snap.exists()) {
      transaction.update(workHoursRef, { entries, lastUpdated: serverTimestamp(), lastManualEdit });
    } else {
      transaction.set(workHoursRef, {
        userId,
        month: normalizedMonth,
        year: String(year),
        entries,
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        lastManualEdit
      });
    }

    return { changedDays: applied.length, applied };
  });
};
